/**
 * Display formatting. No I/O, no React — importable from a test without a DOM.
 */

const RELATIVE = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

const DATE = new Intl.DateTimeFormat('en', { day: 'numeric', month: 'short', year: 'numeric' });

const DATE_TIME = new Intl.DateTimeFormat('en', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

const UNITS: readonly (readonly [Intl.RelativeTimeFormatUnit, number])[] = [
  ['year', 365 * 24 * 60 * 60 * 1000],
  ['month', 30 * 24 * 60 * 60 * 1000],
  ['week', 7 * 24 * 60 * 60 * 1000],
  ['day', 24 * 60 * 60 * 1000],
  ['hour', 60 * 60 * 1000],
  ['minute', 60 * 1000],
];

/**
 * "3 days ago" / "in 2 hours". Returns `null` — never "never" or "—" — for a
 * missing timestamp, so the caller decides what absence means in its column.
 */
export function relativeTime(
  iso: string | null | undefined,
  now: Date = new Date(),
): string | null {
  if (iso === null || iso === undefined || iso === '') return null;
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return null;

  const delta = at - now.getTime();
  const magnitude = Math.abs(delta);
  if (magnitude < 45_000) return 'just now';

  for (const [unit, ms] of UNITS) {
    if (magnitude >= ms) {
      return RELATIVE.format(Math.round(delta / ms), unit);
    }
  }
  return RELATIVE.format(Math.round(delta / 1000), 'second');
}

export function formatDate(iso: string | null | undefined): string | null {
  if (iso === null || iso === undefined || iso === '') return null;
  const at = Date.parse(iso);
  return Number.isNaN(at) ? null : DATE.format(at);
}

export function formatDateTime(iso: string | null | undefined): string | null {
  if (iso === null || iso === undefined || iso === '') return null;
  const at = Date.parse(iso);
  return Number.isNaN(at) ? null : DATE_TIME.format(at);
}

/** `firstName lastName`, collapsed. A contact with no last name is not "X null". */
export function fullName(first: string, last?: string | null): string {
  return [first, last ?? '']
    .map((part) => part.trim())
    .filter((part) => part !== '')
    .join(' ');
}

export function titleCase(value: string): string {
  return value.length === 0 ? value : value[0]?.toUpperCase() + value.slice(1);
}
